import React from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Typography,
  IconButton,
} from "@mui/material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import TwitterIcon from "@mui/icons-material/Twitter";
import FacebookIcon from "@mui/icons-material/Facebook";
import YouTubeIcon from "@mui/icons-material/YouTube";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import ScnLogo from "./assets/img/scnlogo.png";

/* ─── Link data arrays (easy to update with real URLs later) ─── */
const servicesLinks = [
  { label: "Apply Now", to: "/application", external: false },
  { label: "How to Apply", to: "/how-to", external: false },
  { label: "Check Status", to: "/track-status", external: false },
  { label: "Login", to: "/login", external: false },
];

const quickLinks = [
  {
    label: "About Us",
    to: "https://supremecourt.gov.ng/notary-public",
    external: true,
  },
  { label: "Contact Us", to: "/contact", external: false },
  {
    label: "Supreme Court Website",
    to: "https://supremecourt.gov.ng",
    external: true,
  },
  { label: "Privacy Policy", to: "#", external: false },
];

const supportLinks = [
  { label: "Help & FAQs", to: "/how-to", external: false },
  {
    label: "Email Support",
    to: "mailto:notary@supremecourt.gov.ng",
    external: true,
  },
  { label: "Track Application", to: "/track-status", external: false },
];

const socialLinks = [
  { icon: <TwitterIcon fontSize="small" />, href: "#", label: "Twitter" },
  { icon: <FacebookIcon fontSize="small" />, href: "#", label: "Facebook" },
  { icon: <YouTubeIcon fontSize="small" />, href: "#", label: "YouTube" },
  { icon: <LinkedInIcon fontSize="small" />, href: "#", label: "LinkedIn" },
];

/* ─── Reusable footer link ────────────────────────────────────── */
const FooterLink = ({ label, to, external }) => {
  const sharedSx = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "13.5px",
    color: "rgba(255,255,255,0.72)",
    py: "5px",
    cursor: "pointer",
    background: "none",
    border: "none",
    textAlign: "left",
    fontFamily: "'Inter', sans-serif",
    textDecoration: "none",
    transition: "color 0.2s ease",
    "&::before": {
      content: '"›"',
      fontSize: "18px",
      lineHeight: 1,
      color: "#C9A84C",
      opacity: 0.7,
      transition: "opacity 0.2s",
    },
    "&:hover": {
      color: "#F0DFA0",
    },
    "&:hover::before": {
      opacity: 1,
    },
  };

  if (external) {
    return (
      <Box
        component="a"
        href={to}
        target={to.startsWith("http") ? "_blank" : undefined}
        rel={to.startsWith("http") ? "noopener noreferrer" : undefined}
        sx={sharedSx}
      >
        {label}
      </Box>
    );
  }
  return (
    <Box component={Link} to={to} sx={sharedSx}>
      {label}
    </Box>
  );
};

/* ─── Main Footer Component ───────────────────────────────────── */
const Footer = () => {
  const year = new Date().getFullYear();

  return (
    <Box component="footer">
      {/* ── Main footer body ────────────────────────────────────── */}
      <Box className="scn-footer">
        <Container maxWidth="lg">
          <Grid container spacing={5}>
            {/* Col 1 — Brand & Contact */}
            <Grid item xs={12} md={4}>
              {/* Logo + Name */}
              <Box
                className="scn-footer__logo-area"
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5 }}
              >
                <img
                  src={ScnLogo}
                  alt="Supreme Court of Nigeria"
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    border: "2px solid rgba(201,168,76,0.4)",
                  }}
                />
                <Box>
                  <Typography className="scn-footer__brand-name">
                    Supreme Court of Nigeria
                  </Typography>
                  <Typography className="scn-footer__brand-sub">
                    Federal Judiciary — Official Notary Portal
                  </Typography>
                </Box>
              </Box>

              {/* Contact details */}
              <Box className="scn-footer__contact-item">
                <LocationOnOutlinedIcon
                  sx={{ fontSize: 16, color: "#C9A84C", mt: "2px", flexShrink: 0 }}
                />
                <span>Three Arms Zone, PMB 308, Abuja, Nigeria</span>
              </Box>
              <Box className="scn-footer__contact-item">
                <PhoneOutlinedIcon
                  sx={{ fontSize: 16, color: "#C9A84C", flexShrink: 0 }}
                />
                <span>+234 ...</span>
              </Box>
              <Box className="scn-footer__contact-item">
                <EmailOutlinedIcon
                  sx={{ fontSize: 16, color: "#C9A84C", flexShrink: 0 }}
                />
                <Box
                  component="a"
                  href="mailto:notary@supremecourt.gov.ng"
                  sx={{ textDecoration: "none" }}
                >
                  notary@supremecourt.gov.ng
                </Box>
              </Box>

              {/* Social icons */}
              <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                {socialLinks.map((s) => (
                  <IconButton
                    key={s.label}
                    component="a"
                    href={s.href}
                    aria-label={s.label}
                    className="scn-footer__social-btn"
                    size="small"
                  >
                    {s.icon}
                  </IconButton>
                ))}
              </Box>
            </Grid>

            {/* Col 2 — Services */}
            <Grid item xs={6} md={2.5}>
              <Typography className="scn-footer__col-title">Services</Typography>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                {servicesLinks.map((link) => (
                  <FooterLink key={link.label} {...link} />
                ))}
              </Box>
            </Grid>

            {/* Col 3 — Quick Links */}
            <Grid item xs={6} md={2.5}>
              <Typography className="scn-footer__col-title">Quick Links</Typography>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                {quickLinks.map((link) => (
                  <FooterLink key={link.label} {...link} />
                ))}
              </Box>
            </Grid>

            {/* Col 4 — Support */}
            <Grid item xs={12} md={3}>
              <Typography className="scn-footer__col-title">Support</Typography>
              <Box sx={{ display: "flex", flexDirection: "column", mb: 3 }}>
                {supportLinks.map((link) => (
                  <FooterLink key={link.label} {...link} />
                ))}
              </Box>

              {/* Motto */}
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: "10px",
                  border: "1px solid rgba(201,168,76,0.2)",
                  background: "rgba(201,168,76,0.06)",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 11,
                    fontStyle: "italic",
                    color: "rgba(255,255,255,0.5)",
                    letterSpacing: "0.05em",
                    textAlign: "center",
                  }}
                >
                  "Ubi jus ibi remedium"
                </Typography>
                <Typography
                  sx={{
                    fontSize: 10,
                    color: "rgba(255,255,255,0.35)",
                    textAlign: "center",
                    mt: 0.5,
                  }}
                >
                  Where there is a right, there is a remedy.
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Gold divider */}
          <Box className="scn-footer__gold-divider" />
        </Container>
      </Box>

      {/* ── Copyright bar ────────────────────────────────────────── */}
      <Box className="scn-footer__copyright">
        <Container maxWidth="lg">
          <Typography sx={{ fontSize: "inherit", color: "inherit" }}>
            © {year} Supreme Court of Nigeria &nbsp;|&nbsp; Notary Public
            Application Portal &nbsp;|&nbsp; All Rights Reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default Footer;
